
/* =========================================
   LUNA BABY | PREMIUM CART 2.0
   Priority rendering + async secondary updates
   ========================================= */

class CartNotification extends ModalDialog {

  constructor() {
    super();

    this.notification = this.querySelector('#cart-notification');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.cartItemKey = null;
    this.cartUpdateId = 0;
    this.subtotalController = null;
    this.activeElement = null;
    this.secondaryUpdateTimer = null;

    this.setupEvents();
  }

  /* EVENTS */

  setupEvents() {

    this.querySelector('#ModalClose-notification')
      ?.addEventListener('click', () => this.close());

    this.querySelector('.cart-notification__links__continue')
      ?.addEventListener('click', () => this.close());

    const checkoutForm = this.querySelector(
      '#cart-notification-form'
    );

    checkoutForm?.addEventListener('submit', event => {

      const button = checkoutForm.querySelector(
        'button[name="checkout"]'
      );

      if (!button) return;

      if (checkoutForm.dataset.submitting === 'true') {
        event.preventDefault();
        return;
      }

      checkoutForm.dataset.submitting = 'true';
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');
    });
  }

  /* OPEN */

  open() {
    document.body.removeEventListener(
      'click',
      this.onBodyClick
    );

    document.body.addEventListener(
      'click',
      this.onBodyClick
    );

    this.show(null);
  }

  /* CLOSE */

  close() {
    document.body.removeEventListener(
      'click',
      this.onBodyClick
    );

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    this.hide();
  }

  /* PRIORITY RENDER */

  renderContents(parsedState) {

    if (!parsedState) return;

    this.cartItemKey = parsedState.key;
    const updateId = ++this.cartUpdateId;

    clearTimeout(this.secondaryUpdateTimer);
    this.subtotalController?.abort();

    const sections = this.getSectionsToRender();

    // Update product information first
    const productSection = sections.find(
      section => section.id === 'cart-notification-product'
    );

    if (productSection) {
      this.renderSection(productSection, parsedState);
    }

    // Open modal immediately after rendering product
    this.open();

    // Process remaining sections without blocking the open call
    this.secondaryUpdateTimer = setTimeout(() => {

      if (updateId !== this.cartUpdateId) return;

      for (const section of sections) {
        if (section.id === 'cart-notification-product') {
          continue;
        }

        this.renderSection(section, parsedState);
      }

      this.updateSubtotal(updateId);

    }, 0);
  }

  /* RENDER SINGLE SECTION */

  renderSection(section, parsedState) {

    const sectionHTML = parsedState.sections?.[section.id];
    if (!sectionHTML) return;

    const extractedHTML = this.getSectionInnerHTML(
      sectionHTML,
      section.selector
    );

    if (extractedHTML === null) return;

    const target = document.getElementById(section.id);

    if (target) {
      target.innerHTML = extractedHTML;
    }

    if (section.id === 'cart-free-delivery') {

      const productDelivery = document.querySelector(
        '#product-page-free-delivery'
      );

      if (productDelivery) {
        productDelivery.innerHTML = extractedHTML;
      }
    }
  }

  /* SHOPIFY SECTIONS */

  getSectionsToRender() {

    const sections = [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`
      },
      {
        id: 'cart-notification-button'
      },
      {
        id: 'cart-icon-bubble'
      },
      {
        id: 'cart-icon-bubble--mobile'
      },
      {
        id: 'cart-free-delivery'
      }
    ];

    return sections.filter(section => {

      if (document.getElementById(section.id)) {
        return true;
      }

      return (
        section.id === 'cart-free-delivery' &&
        !!document.getElementById('product-page-free-delivery')
      );
    });
  }

  /* HTML EXTRACTION */

  getSectionInnerHTML(html, selector = '.shopify-section') {

    const fragment = new DOMParser()
      .parseFromString(html, 'text/html');

    const element = fragment.querySelector(selector);

    return element ? element.innerHTML : null;
  }

  /* SUBTOTAL */

  async updateSubtotal(updateId) {

    const subtotalElement = this.querySelector(
      '#premium-cart-total'
    );

    if (!subtotalElement) return;

    this.subtotalController?.abort();

    const controller = new AbortController();
    this.subtotalController = controller;

    try {

      const root = window.Shopify?.routes?.root || '/';

      const response = await fetch(`${root}cart.js`, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`Cart request: ${response.status}`);
      }

      const cart = await response.json();

      if (updateId !== this.cartUpdateId) return;

      subtotalElement.textContent = this.formatCartMoney(
        cart.total_price,
        cart.currency
      );

    } catch (error) {

      if (error.name !== 'AbortError') {
        console.warn(
          '[Luna Cart] Subtotal update failed:',
          error
        );
      }

    } finally {

      if (this.subtotalController === controller) {
        this.subtotalController = null;
      }
    }
  }

  /* MONEY */

  formatCartMoney(amount, currency) {

    const activeCurrency =
      currency ||
      window.Shopify?.currency?.active ||
      'USD';

    const locale = document.documentElement.lang || 'en-US';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: activeCurrency
      }).format(amount / 100);

    } catch (error) {
      return (amount / 100).toFixed(2);
    }
  }

  /* CLICK OUTSIDE */

  handleBodyClick(event) {

    const target = event.target;

    if (
      target !== this.notification &&
      !target.closest('cart-notification')
    ) {

      const disclosure = target.closest('details-disclosure');

      this.activeElement = disclosure
        ? disclosure.querySelector('summary')
        : null;

      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* CLEANUP */

  disconnectedCallback() {

    document.body.removeEventListener(
      'click',
      this.onBodyClick
    );

    clearTimeout(this.secondaryUpdateTimer);
    this.subtotalController?.abort();

    if (super.disconnectedCallback) {
      super.disconnectedCallback();
    }
  }
}

/* REGISTER */

if (!customElements.get('cart-notification')) {
  customElements.define(
    'cart-notification',
    CartNotification
  );
}
